/**
 * <collection-card-variants>
 *
 * Per-card variant picker for the collection product grid.
 * Set localStorage.collectionCardVariantsDebug = '1' for verbose event tracing.
 */

const DEBUG = true;

/** @param {string} productId @param {string} step @param {unknown} [data] */
function debugLog(productId, step, data) {
  if (!DEBUG && localStorage.getItem('collectionCardVariantsDebug') !== '1') return;
  const verbose = localStorage.getItem('collectionCardVariantsDebug') === '1';
  if (step === 'event' && !verbose) return;
  const prefix = `[collection-card-variants][${productId || '?'}]`;
  if (data === undefined) {
    console.log(prefix, step);
  } else {
    console.log(prefix, step, data);
  }
}

if (!customElements.get('collection-card-variants')) {
  customElements.define(
    'collection-card-variants',
    class CollectionCardVariants extends HTMLElement {
      /** @type {string} */
      #productId = '?';

      connectedCallback() {
        this.card = this.closest('.collection-card, .bestsellers__card');
        if (!this.card) {
          console.warn('[collection-card-variants] no .collection-card / .bestsellers__card ancestor');
          return;
        }

        this.#productId = this.card.dataset.productId || '?';

        this.variants = this.#parseVariants();
        if (!this.variants.length) {
          debugLog(this.#productId, 'abort:no-variants');
          return;
        }

        this.image = this.card.querySelector('[data-card-image]');
        this.links = this.card.querySelectorAll('[data-card-link]');
        this.priceEl = this.card.querySelector('[data-card-price]');
        this.variantIdInput = this.card.querySelector('[data-card-variant-id]');
        this.addToCartButton = this.card.querySelector('[ref="addToCartButton"]');
        this.addToCartTextEl = this.addToCartButton?.querySelector('.add-to-cart-text__content');
        this.addToCartText = this.dataset.addToCartText || this.addToCartTextEl?.textContent.trim() || 'Add to cart';
        this.soldOutText = this.dataset.soldOutText || 'Sold out';

        this.activeVariant =
          this.variants.find((v) => v.options.every((value, i) => this.#initialSelection()[i] === value)) ||
          this.variants[0];

        if (this.image) {
          this.originalSrc = this.image.getAttribute('src');
          this.originalSrcset = this.image.getAttribute('srcset');
        }

        const radios = this.querySelectorAll('input[type="radio"][data-option-position]');
        const selects = this.querySelectorAll('select[data-option-position]');

        debugLog(this.#productId, 'connected', {
          variantCount: this.variants.length,
          activeVariantId: this.activeVariant?.id,
          pickerRadios: radios.length,
          pickerSelects: selects.length,
          hasVariantIdInput: !!this.variantIdInput,
          hasAddToCartButton: !!this.addToCartButton,
          initialSelection: this.#selectedOptions(),
          variantOptionsSample: this.variants.map((v) => v.options),
        });

        this._onChange = (event) => this.#onChange(event);
        this._onClick = (event) => this.#onClick(event);
        this._onDebugEvent = (event) => this.#onDebugEvent(event);

        // Bubble: normal change path
        this.addEventListener('change', this._onChange);

        // Capture: radios sometimes never bubble change (overlay / label stacking)
        this.addEventListener('click', this._onClick, true);

        if (DEBUG || localStorage.getItem('collectionCardVariantsDebug') === '1') {
          this.addEventListener('change', this._onDebugEvent, true);
          this.addEventListener('click', this._onDebugEvent, true);
          this.addEventListener('input', this._onDebugEvent, true);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this._onChange);
        this.removeEventListener('click', this._onClick, true);
        if (DEBUG || localStorage.getItem('collectionCardVariantsDebug') === '1') {
          this.removeEventListener('change', this._onDebugEvent, true);
          this.removeEventListener('click', this._onDebugEvent, true);
          this.removeEventListener('input', this._onDebugEvent, true);
        }
      }

      #onDebugEvent(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const input = target.closest('input[type="radio"][data-option-position], select[data-option-position]');
        if (!input && !target.matches('label.variant-option__button-label')) return;

        debugLog(this.#productId, 'event', {
          type: event.type,
          phase: event.eventPhase === 1 ? 'capture' : event.eventPhase === 2 ? 'target' : 'bubble',
          target: target.tagName,
          targetClass: target.className?.slice?.(0, 60),
          inputChecked: input instanceof HTMLInputElement ? input.checked : undefined,
          inputValue: input instanceof HTMLInputElement ? input.value : undefined,
          optionValue: input instanceof HTMLElement ? input.dataset.optionValue : undefined,
        });
      }

      #parseVariants() {
        const node = this.querySelector('[data-card-variants]');
        if (!node) {
          debugLog(this.#productId, 'parse:missing-json-node');
          return [];
        }
        try {
          return JSON.parse(node.textContent) || [];
        } catch (e) {
          console.warn('[collection-card-variants] failed to parse variant JSON', e, node.textContent?.slice(0, 120));
          return [];
        }
      }

      #initialSelection() {
        return this.#selectedOptions();
      }

      /** @returns {string[]} */
      #selectedOptions() {
        const selection = [];

        this.querySelectorAll('input[type="radio"][data-option-position]:checked').forEach((input) => {
          const pos = Number(input.dataset.optionPosition);
          if (pos > 0) selection[pos - 1] = this.#readOptionValue(input);
        });

        this.querySelectorAll('select[data-option-position]').forEach((select) => {
          const pos = Number(select.dataset.optionPosition);
          const opt = select.selectedOptions[0];
          if (pos > 0 && opt) selection[pos - 1] = opt.dataset.optionValue || opt.value;
        });

        return selection;
      }

      /** @param {HTMLInputElement} input */
      #readOptionValue(input) {
        return input.dataset.optionValue || input.value || '';
      }

      /** @param {string[]} selection */
      #findVariant(selection) {
        return this.variants.find((v) => {
          const opts = v.options || [];
          return opts.every((value, i) => value === selection[i]);
        });
      }

      /** @param {Event} event */
      #onChange(event) {
        debugLog(this.#productId, 'change:received', {
          target: event.target instanceof Element ? event.target.tagName : typeof event.target,
        });

        const picker = this.#resolvePicker(event.target);
        if (!picker) {
          debugLog(this.#productId, 'change:ignored-not-picker', {
            target: event.target instanceof Element ? event.target.outerHTML?.slice(0, 80) : event.target,
          });
          return;
        }

        this.#applyPickerChange(picker, 'change');
      }

      /** @param {Event} event */
      #onClick(event) {
        const picker = this.#resolvePicker(event.target);
        if (!picker) return;

        debugLog(this.#productId, 'click:on-picker', {
          tag: picker.tagName,
          value: picker instanceof HTMLInputElement ? picker.value : undefined,
        });

        // Run after the browser updates :checked (change may not fire on some swatch layouts)
        queueMicrotask(() => {
          if (picker instanceof HTMLInputElement && !picker.checked) return;
          this.#applyPickerChange(picker, 'click');
        });
      }

      /** @param {EventTarget | null} target */
      #resolvePicker(target) {
        if (!(target instanceof Element)) return null;

        if (target instanceof HTMLInputElement && target.matches('input[type="radio"][data-option-position]')) {
          return target;
        }
        if (target instanceof HTMLSelectElement && target.matches('select[data-option-position]')) {
          return target;
        }

        const fromLabel = target.closest('label.variant-option__button-label');
        if (fromLabel) {
          const input = fromLabel.querySelector('input[type="radio"][data-option-position]');
          if (input instanceof HTMLInputElement) return input;
        }

        return target.closest('input[type="radio"][data-option-position], select[data-option-position]');
      }

      /** @param {HTMLInputElement | HTMLSelectElement} picker @param {'change' | 'click'} source */
      #applyPickerChange(picker, source) {
        const selectedValue =
          picker instanceof HTMLSelectElement
            ? picker.selectedOptions[0]?.dataset.optionValue || picker.selectedOptions[0]?.value
            : this.#readOptionValue(picker);

        debugLog(this.#productId, 'apply', {
          source,
          optionPosition: picker.dataset.optionPosition,
          selectedValue,
          pickerValue: picker instanceof HTMLInputElement ? picker.value : undefined,
          datasetOptionValue: picker instanceof HTMLInputElement ? picker.dataset.optionValue : undefined,
        });

        if (picker instanceof HTMLInputElement) this.#syncCurrentChecked(picker);

        this.#updateSwatchValueLabels();

        const selection = this.#selectedOptions();
        const variant = this.#findVariant(selection);

        debugLog(this.#productId, 'match', {
          selection,
          matchedId: variant?.id ?? null,
          matchedOptions: variant?.options ?? null,
        });

        if (!variant) {
          console.warn(`[collection-card-variants][${this.#productId}] no matching variant`, {
            selection,
            availableVariants: this.variants.map((v) => ({ id: v.id, options: v.options })),
          });
          this.#updateForm(null);
          return;
        }

        if (variant.id === this.activeVariant?.id) {
          debugLog(this.#productId, 'apply:skip-same-variant', { source });
          return;
        }

        this.activeVariant = variant;
        this.#updateImage(variant);
        this.#updatePrice(variant);
        this.#updateLinks(variant);
        this.#updateForm(variant);

        debugLog(this.#productId, 'updated', {
          variantId: variant.id,
          price: variant.price,
          formVariantId: this.variantIdInput?.value,
        });
      }

      #updateForm(variant) {
        const isAvailable = Boolean(variant && variant.available);

        if (this.variantIdInput instanceof HTMLInputElement) {
          this.variantIdInput.value = (variant && String(variant.id)) || '';
          this.variantIdInput.disabled = !isAvailable;
        } else {
          debugLog(this.#productId, 'warn:missing-variant-input');
        }

        if (this.addToCartButton instanceof HTMLButtonElement) {
          this.addToCartButton.disabled = !isAvailable;
        }

        if (this.addToCartTextEl instanceof HTMLElement) {
          this.addToCartTextEl.textContent = isAvailable ? this.addToCartText : this.soldOutText;
        }
      }

      /**
       * Update the selected-value text shown in each swatch fieldset's <legend>
       * (e.g. "Color: Blue") to reflect the currently-checked option.
       */
      #updateSwatchValueLabels() {
        this.querySelectorAll('fieldset[data-option-position] .variant-option__swatch-value').forEach((label) => {
          const fieldset = label.closest('fieldset[data-option-position]');
          if (!fieldset) return;
          const checked = fieldset.querySelector('input[type="radio"][data-option-position]:checked');
          if (checked instanceof HTMLInputElement) {
            label.textContent = checked.dataset.optionValue || checked.value || label.textContent;
          }
        });
      }

      /** @param {HTMLInputElement} target */
      #syncCurrentChecked(target) {
        const group = target.closest('fieldset[data-option-position]');
        if (!group) return;
        group.querySelectorAll('input[type="radio"][data-option-position]').forEach((input) => {
          input.dataset.currentChecked = input.checked ? 'true' : 'false';
        });
      }

      #updateImage(variant) {
        if (!this.image) return;

        if (variant.image) {
          this.image.setAttribute('src', variant.image);
          this.image.removeAttribute('srcset');
        } else if (this.originalSrc) {
          this.image.setAttribute('src', this.originalSrc);
          if (this.originalSrcset) this.image.setAttribute('srcset', this.originalSrcset);
        }
      }

      #updatePrice(variant) {
        if (this.priceEl && variant.price) {
          // Rebrand: keep the leading currency symbol wrapped (smaller "$"), matching snippets/price-currency.liquid
          const price = variant.price.replace(/^([^\d\s.,]+)/, '<span class="price-currency">$1</span>');
          this.priceEl.innerHTML = '<span>' + price + '</span>';
        }
      }

      #updateLinks(variant) {
        if (!variant.url) return;
        this.links.forEach((link) => link.setAttribute('href', variant.url));
      }
    }
  );
}
